import React from 'react';
import { 
  FaUser, FaMoneyBillWave, FaPiggyBank, FaHome, FaShieldAlt, 
  FaBullseye, FaHeart, FaChild, FaWallet, FaChartPie, FaRing,
  FaCar, FaGraduationCap, FaUmbrellaBeach, FaGlobe, FaPlus, FaTrash
} from 'react-icons/fa';
import InputField from '../shared/InputField';
import LoanProtectionToggle from '../shared/LoanProtectionToggle';

const ClientDataForm = ({ 
  formData, 
  activeTab, 
  handleInputChange, 
  handleAddChild, 
  handleRemoveChild, 
  handleChildChange,
  handleAddChildGoal,
  handleRemoveChildGoal,
  handleChildGoalChange,
  handleAddOtherGoal,
  handleRemoveOtherGoal,
  handleOtherGoalChange,
  formatCurrency 
}) => {
  switch (activeTab) {
    case 0: // Personal Info
      return (
        <div className="space-y-6" data-testid="personal-info-section">
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center">
            <FaUser className="mr-2 text-[#FFD700]" /> Personal Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField label="Full Name *" value={formData.name} onChange={(v) => handleInputChange('name', v)} placeholder="Enter your full name" />
            <InputField label="Email *" type="email" value={formData.email} onChange={(v) => handleInputChange('email', v)} placeholder="Enter your email" />
            <InputField label="Phone Number *" type="tel" value={formData.phone} onChange={(v) => handleInputChange('phone', v)} placeholder="10-digit mobile number" />
            <InputField label="Date of Birth" type="date" value={formData.date_of_birth} onChange={(v) => handleInputChange('date_of_birth', v)} />
            <InputField label="Age" type="number" value={formData.age} onChange={(v) => handleInputChange('age', v)} placeholder="Your age" />
            <InputField label="PAN Number" value={formData.pan_number} onChange={(v) => handleInputChange('pan_number', v.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
            <InputField label="Occupation" value={formData.occupation} onChange={(v) => handleInputChange('occupation', v)} placeholder="e.g., Business, Service, Professional" />
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1">Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-4 py-2 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                rows={2}
                placeholder="Enter your address"
              />
            </div>
          </div>

          {/* Family Information */}
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center mt-8">
            <FaHeart className="mr-2 text-[#FFD700]" /> Family Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField label="Spouse Name" value={formData.spouse_name} onChange={(v) => handleInputChange('spouse_name', v)} placeholder="Enter spouse name (if applicable)" />
            <InputField label="Spouse Date of Birth" type="date" value={formData.spouse_dob} onChange={(v) => handleInputChange('spouse_dob', v)} />
            <InputField label="Spouse Occupation" value={formData.spouse_occupation} onChange={(v) => handleInputChange('spouse_occupation', v)} placeholder="Spouse's occupation" />
            <InputField label="Total Dependents" type="number" value={formData.total_dependents} onChange={(v) => handleInputChange('total_dependents', parseInt(v) || 0)} min={0} />
          </div>

          {/* Children */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium flex items-center"><FaChild className="mr-2 text-[#FFD700]" /> Children Details</h4>
              <button
                onClick={handleAddChild}
                className="px-3 py-1.5 bg-[#1E3A5F] text-[#FFD700] rounded-lg hover:bg-[#2D5A8F] transition-all flex items-center space-x-1 text-sm"
                data-testid="add-child-btn"
              >
                <FaPlus size={12} />
                <span>Add Child</span>
              </button>
            </div>
            
            {formData.children.length === 0 ? (
              <p className="text-gray-500 text-sm">No children added. Click "Add Child" to add details.</p>
            ) : (
              <div className="space-y-3">
                {formData.children.map((child, index) => (
                  <div key={index} className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[#FFD700] font-medium">Child {index + 1}</span>
                      <button onClick={() => handleRemoveChild(index)} className="text-red-400 hover:text-red-300">
                        <FaTrash size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <InputField label="Name" value={child.name} onChange={(v) => handleChildChange(index, 'name', v)} placeholder="Child's name" small />
                      <InputField label="Age" type="number" value={child.age} onChange={(v) => handleChildChange(index, 'age', v)} placeholder="Age" small />
                      <InputField label="Education Goal (₹)" type="number" value={child.education_goal} onChange={(v) => handleChildChange(index, 'education_goal', v)} placeholder="Amount" small />
                      <InputField label="Marriage Goal (₹)" type="number" value={child.marriage_goal} onChange={(v) => handleChildChange(index, 'marriage_goal', v)} placeholder="Amount" small />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );

    case 1: // Income & Expenses
      return (
        <div className="space-y-6" data-testid="income-section">
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center">
            <FaMoneyBillWave className="mr-2 text-green-400" /> Income Details
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField label="Annual Income (₹)" type="number" value={formData.annual_income} onChange={(v) => handleInputChange('annual_income', v)} placeholder="Your annual income" />
            <InputField label="Monthly Income (₹)" type="number" value={formData.monthly_income} onChange={(v) => handleInputChange('monthly_income', v)} placeholder="Your monthly income" />
            <InputField label="Other Income (₹/month)" type="number" value={formData.other_income} onChange={(v) => handleInputChange('other_income', v)} placeholder="Other sources" />
            <InputField label="Rental Income (₹/month)" type="number" value={formData.rental_income} onChange={(v) => handleInputChange('rental_income', v)} placeholder="Rental income" />
            <InputField label="Interest Income (₹/year)" type="number" value={formData.interest_income} onChange={(v) => handleInputChange('interest_income', v)} placeholder="FD, savings interest" />
            <InputField label="Dividend Income (₹/year)" type="number" value={formData.dividend_income} onChange={(v) => handleInputChange('dividend_income', v)} placeholder="Stock dividends" />
          </div>

          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center mt-8">
            <FaWallet className="mr-2 text-red-400" /> Monthly Expenses
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField label="Total Monthly Expenses (₹)" type="number" value={formData.monthly_expenses} onChange={(v) => handleInputChange('monthly_expenses', v)} placeholder="Total expenses" />
            <InputField label="Rent / EMI (₹)" type="number" value={formData.rent_emi} onChange={(v) => handleInputChange('rent_emi', v)} placeholder="Rent or home EMI" />
            <InputField label="Utilities (₹)" type="number" value={formData.utilities} onChange={(v) => handleInputChange('utilities', v)} placeholder="Electric, water, etc." />
            <InputField label="Education Expenses (₹)" type="number" value={formData.education_expenses} onChange={(v) => handleInputChange('education_expenses', v)} placeholder="School, tuition fees" />
            <InputField label="Medical Expenses (₹)" type="number" value={formData.medical_expenses} onChange={(v) => handleInputChange('medical_expenses', v)} placeholder="Healthcare costs" />
            <InputField label="Lifestyle Expenses (₹)" type="number" value={formData.lifestyle_expenses} onChange={(v) => handleInputChange('lifestyle_expenses', v)} placeholder="Entertainment, dining" />
          </div>
        </div>
      );

    case 2: // Assets
      return (
        <div className="space-y-6" data-testid="assets-section">
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center">
            <FaPiggyBank className="mr-2 text-[#FFD700]" /> Your Assets
          </h3>
          
          <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] mb-4">
            <p className="text-gray-400 text-sm">Enter the current value of your assets. This helps us calculate your net worth and plan better.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InputField label="Savings Account (₹)" type="number" value={formData.savings_account} onChange={(v) => handleInputChange('savings_account', v)} placeholder="Bank savings" icon={<FaWallet className="text-blue-400" />} />
            <InputField label="Fixed Deposits (₹)" type="number" value={formData.fixed_deposits} onChange={(v) => handleInputChange('fixed_deposits', v)} placeholder="FD value" icon={<FaPiggyBank className="text-green-400" />} />
            <InputField label="Mutual Funds (₹)" type="number" value={formData.mutual_funds} onChange={(v) => handleInputChange('mutual_funds', v)} placeholder="MF investments" icon={<FaChartPie className="text-purple-400" />} />
            <InputField label="Stocks (₹)" type="number" value={formData.stocks} onChange={(v) => handleInputChange('stocks', v)} placeholder="Stock portfolio" icon={<FaChartPie className="text-cyan-400" />} />
            <InputField label="PPF/EPF (₹)" type="number" value={formData.ppf_epf} onChange={(v) => handleInputChange('ppf_epf', v)} placeholder="Provident fund" icon={<FaShieldAlt className="text-orange-400" />} />
            <InputField label="Real Estate Value (₹)" type="number" value={formData.real_estate_value} onChange={(v) => handleInputChange('real_estate_value', v)} placeholder="Property value" icon={<FaHome className="text-yellow-400" />} />
            <InputField label="Gold/Jewelry (₹)" type="number" value={formData.gold_jewelry} onChange={(v) => handleInputChange('gold_jewelry', v)} placeholder="Gold & jewelry" icon={<FaRing className="text-[#FFD700]" />} />
            <InputField label="Other Assets (₹)" type="number" value={formData.other_assets} onChange={(v) => handleInputChange('other_assets', v)} placeholder="Other valuables" />
          </div>

          {/* Total Assets Summary */}
          <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 rounded-lg p-4 border border-green-500/50 mt-6">
            <div className="flex items-center justify-between">
              <span className="text-white font-medium">Total Estimated Assets</span>
              <span className="text-2xl font-bold text-green-400">
                {formatCurrency(
                  (parseInt(formData.savings_account) || 0) +
                  (parseInt(formData.fixed_deposits) || 0) +
                  (parseInt(formData.mutual_funds) || 0) +
                  (parseInt(formData.stocks) || 0) +
                  (parseInt(formData.ppf_epf) || 0) +
                  (parseInt(formData.real_estate_value) || 0) +
                  (parseInt(formData.gold_jewelry) || 0) +
                  (parseInt(formData.other_assets) || 0)
                )}
              </span>
            </div>
          </div>
        </div>
      );

    case 3: // Liabilities
      return (
        <div className="space-y-6" data-testid="liabilities-section">
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center">
            <FaHome className="mr-2 text-red-400" /> Your Liabilities
          </h3>
          
          <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] mb-4">
            <p className="text-gray-400 text-sm">Enter your outstanding loans and their monthly EMIs. Mark loans as <span className="text-green-400 font-medium">Protected</span> if they are covered by insurance (loan protection plan).</p>
          </div>
          
          <div className="space-y-4">
            {/* Home Loan */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium flex items-center"><FaHome className="mr-2 text-[#FFD700]" /> Home Loan</h4>
                <LoanProtectionToggle 
                  isProtected={formData.home_loan_protected} 
                  onChange={(v) => handleInputChange('home_loan_protected', v)} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Outstanding Amount (₹)" type="number" value={formData.home_loan} onChange={(v) => handleInputChange('home_loan', v)} placeholder="Outstanding principal" small />
                <InputField label="Monthly EMI (₹)" type="number" value={formData.home_loan_emi} onChange={(v) => handleInputChange('home_loan_emi', v)} placeholder="EMI amount" small />
              </div>
            </div>

            {/* Car Loan */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium flex items-center"><FaCar className="mr-2 text-[#FFD700]" /> Car Loan</h4>
                <LoanProtectionToggle 
                  isProtected={formData.car_loan_protected} 
                  onChange={(v) => handleInputChange('car_loan_protected', v)} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Outstanding Amount (₹)" type="number" value={formData.car_loan} onChange={(v) => handleInputChange('car_loan', v)} placeholder="Outstanding principal" small />
                <InputField label="Monthly EMI (₹)" type="number" value={formData.car_loan_emi} onChange={(v) => handleInputChange('car_loan_emi', v)} placeholder="EMI amount" small />
              </div>
            </div>

            {/* Personal Loan */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium flex items-center"><FaWallet className="mr-2 text-[#FFD700]" /> Personal Loan</h4>
                <LoanProtectionToggle 
                  isProtected={formData.personal_loan_protected} 
                  onChange={(v) => handleInputChange('personal_loan_protected', v)} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Outstanding Amount (₹)" type="number" value={formData.personal_loan} onChange={(v) => handleInputChange('personal_loan', v)} placeholder="Outstanding principal" small />
                <InputField label="Monthly EMI (₹)" type="number" value={formData.personal_loan_emi} onChange={(v) => handleInputChange('personal_loan_emi', v)} placeholder="EMI amount" small />
              </div>
            </div>

            {/* Education Loan */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium flex items-center"><FaGraduationCap className="mr-2 text-[#FFD700]" /> Education Loan</h4>
                <LoanProtectionToggle 
                  isProtected={formData.education_loan_protected} 
                  onChange={(v) => handleInputChange('education_loan_protected', v)} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Outstanding Amount (₹)" type="number" value={formData.education_loan} onChange={(v) => handleInputChange('education_loan', v)} placeholder="Outstanding principal" small />
                <InputField label="Monthly EMI (₹)" type="number" value={formData.education_loan_emi} onChange={(v) => handleInputChange('education_loan_emi', v)} placeholder="EMI amount" small />
              </div>
            </div>

            {/* Other */}
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Credit Card Outstanding (₹)" type="number" value={formData.credit_card_outstanding} onChange={(v) => handleInputChange('credit_card_outstanding', v)} placeholder="Total CC dues" />
              <InputField label="Other Liabilities (₹)" type="number" value={formData.other_liabilities} onChange={(v) => handleInputChange('other_liabilities', v)} placeholder="Any other loans" />
            </div>
          </div>

          {/* Total Liabilities Summary */}
          <div className="bg-gradient-to-r from-red-900/30 to-red-800/30 rounded-lg p-4 border border-red-500/50 mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">Total Liabilities</span>
              <span className="text-2xl font-bold text-red-400">
                {formatCurrency(
                  (parseInt(formData.home_loan) || 0) +
                  (parseInt(formData.car_loan) || 0) +
                  (parseInt(formData.personal_loan) || 0) +
                  (parseInt(formData.education_loan) || 0) +
                  (parseInt(formData.credit_card_outstanding) || 0) +
                  (parseInt(formData.other_liabilities) || 0)
                )}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Protected Loans</span>
              <span className="text-green-400 font-medium">
                {formatCurrency(
                  (formData.home_loan_protected ? (parseInt(formData.home_loan) || 0) : 0) +
                  (formData.car_loan_protected ? (parseInt(formData.car_loan) || 0) : 0) +
                  (formData.personal_loan_protected ? (parseInt(formData.personal_loan) || 0) : 0) +
                  (formData.education_loan_protected ? (parseInt(formData.education_loan) || 0) : 0)
                )}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Unprotected Loans</span>
              <span className="text-yellow-400 font-medium">
                {formatCurrency(
                  (!formData.home_loan_protected ? (parseInt(formData.home_loan) || 0) : 0) +
                  (!formData.car_loan_protected ? (parseInt(formData.car_loan) || 0) : 0) +
                  (!formData.personal_loan_protected ? (parseInt(formData.personal_loan) || 0) : 0) +
                  (!formData.education_loan_protected ? (parseInt(formData.education_loan) || 0) : 0) +
                  (parseInt(formData.credit_card_outstanding) || 0) +
                  (parseInt(formData.other_liabilities) || 0)
                )}
              </span>
            </div>
          </div>
        </div>
      );

    case 4: // Insurance
      return (
        <div className="space-y-6" data-testid="insurance-section">
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center">
            <FaShieldAlt className="mr-2 text-blue-400" /> Insurance Coverage
          </h3>
          
          <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] mb-4">
            <p className="text-gray-400 text-sm">Enter your current insurance coverage. We'll analyze if you have adequate protection for your family.</p>
          </div>
          
          <div className="space-y-4">
            {/* Life Insurance */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <h4 className="text-white font-medium mb-3 flex items-center"><FaHeart className="mr-2 text-red-400" /> Life Insurance</h4>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Sum Assured (₹)" type="number" value={formData.life_insurance_cover} onChange={(v) => handleInputChange('life_insurance_cover', v)} placeholder="Total cover amount" small />
                <InputField label="Annual Premium (₹)" type="number" value={formData.life_insurance_premium} onChange={(v) => handleInputChange('life_insurance_premium', v)} placeholder="Annual premium" small />
              </div>
            </div>

            {/* Term Insurance */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <h4 className="text-white font-medium mb-3 flex items-center"><FaShieldAlt className="mr-2 text-green-400" /> Term Insurance</h4>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Sum Assured (₹)" type="number" value={formData.term_insurance_cover} onChange={(v) => handleInputChange('term_insurance_cover', v)} placeholder="Total cover amount" small />
                <InputField label="Annual Premium (₹)" type="number" value={formData.term_insurance_premium} onChange={(v) => handleInputChange('term_insurance_premium', v)} placeholder="Annual premium" small />
              </div>
            </div>

            {/* Health Insurance */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <h4 className="text-white font-medium mb-3 flex items-center"><FaHeart className="mr-2 text-pink-400" /> Health Insurance</h4>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Sum Insured (₹)" type="number" value={formData.health_insurance_cover} onChange={(v) => handleInputChange('health_insurance_cover', v)} placeholder="Total cover amount" small />
                <InputField label="Annual Premium (₹)" type="number" value={formData.health_insurance_premium} onChange={(v) => handleInputChange('health_insurance_premium', v)} placeholder="Annual premium" small />
              </div>
            </div>

            {/* Vehicle Insurance */}
            <InputField label="Vehicle Insurance Premium (₹/year)" type="number" value={formData.vehicle_insurance} onChange={(v) => handleInputChange('vehicle_insurance', v)} placeholder="Annual vehicle insurance" />
          </div>

          {/* Insurance Summary */}
          <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-lg p-4 border border-blue-500/50 mt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-400 text-sm">Total Life Cover</span>
                <p className="text-xl font-bold text-blue-400">
                  {formatCurrency((parseInt(formData.life_insurance_cover) || 0) + (parseInt(formData.term_insurance_cover) || 0))}
                </p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Total Annual Premium</span>
                <p className="text-xl font-bold text-white">
                  {formatCurrency(
                    (parseInt(formData.life_insurance_premium) || 0) +
                    (parseInt(formData.term_insurance_premium) || 0) +
                    (parseInt(formData.health_insurance_premium) || 0) +
                    (parseInt(formData.vehicle_insurance) || 0)
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      );

    case 5: // Goals
      return (
        <div className="space-y-6" data-testid="goals-section">
          <h3 className="text-lg font-bold text-white border-b border-[#2D5A8F] pb-2 flex items-center">
            <FaBullseye className="mr-2 text-[#FFD700]" /> Financial Goals
          </h3>
          
          <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] mb-4">
            <p className="text-gray-400 text-sm">Tell us about your financial goals. Your advisor will help create a detailed plan to achieve them.</p>
          </div>
          
          <div className="space-y-4">
            {/* Retirement */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <h4 className="text-white font-medium mb-3 flex items-center"><FaUmbrellaBeach className="mr-2 text-orange-400" /> Retirement Planning</h4>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Desired Retirement Age" type="number" value={formData.retirement_age} onChange={(v) => handleInputChange('retirement_age', v)} placeholder="e.g., 60" small />
                <InputField label="Monthly Expense at Retirement (₹)" type="number" value={formData.retirement_monthly_expense} onChange={(v) => handleInputChange('retirement_monthly_expense', v)} placeholder="Expected monthly expense" small />
              </div>
            </div>

            {/* Child Goals - Dynamic Section */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-medium flex items-center">
                  <FaChild className="mr-2 text-blue-400" /> Child Goals (Education / Marriage)
                </h4>
                <button
                  onClick={handleAddChildGoal}
                  className="px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-1 text-sm font-bold"
                  data-testid="add-child-goal-btn"
                >
                  <FaPlus size={12} />
                  <span>Add Child Goal</span>
                </button>
              </div>
              
              {(!formData.child_goals || formData.child_goals.length === 0) ? (
                <p className="text-gray-500 text-sm text-center py-4">No child goals added. Click "+ Add Child Goal" to add education or marriage goals for your children.</p>
              ) : (
                <div className="space-y-4">
                  {formData.child_goals.map((goal, index) => (
                    <div key={index} className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[#FFD700] font-medium">Child Goal {index + 1}</span>
                        <button onClick={() => handleRemoveChildGoal(index)} className="text-red-400 hover:text-red-300 p-1">
                          <FaTrash size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <InputField label="Child Name" value={goal.child_name} onChange={(v) => handleChildGoalChange(index, 'child_name', v)} placeholder="Child's name" small />
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Goal Type</label>
                          <select
                            value={goal.goal_type}
                            onChange={(e) => handleChildGoalChange(index, 'goal_type', e.target.value)}
                            className="w-full px-3 py-2 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white text-sm focus:border-[#FFD700] focus:outline-none"
                          >
                            <option value="education">Education</option>
                            <option value="marriage">Marriage</option>
                            <option value="both">Both</option>
                          </select>
                        </div>
                        <InputField label="Target Amount (₹)" type="number" value={goal.target_amount} onChange={(v) => handleChildGoalChange(index, 'target_amount', v)} placeholder="Goal amount" small />
                        <InputField label="Target Year" type="number" value={goal.target_year} onChange={(v) => handleChildGoalChange(index, 'target_year', v)} placeholder="e.g., 2035" small />
                        <InputField label="Current Savings (₹)" type="number" value={goal.current_savings} onChange={(v) => handleChildGoalChange(index, 'current_savings', v)} placeholder="Already saved" small />
                        <InputField label="Notes" value={goal.notes} onChange={(v) => handleChildGoalChange(index, 'notes', v)} placeholder="Any notes" small />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Standard Goals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Home Purchase Goal (₹)" type="number" value={formData.home_purchase_goal} onChange={(v) => handleInputChange('home_purchase_goal', v)} placeholder="Dream home cost" icon={<FaHome className="text-yellow-400" />} />
              <InputField label="Vacation Fund (₹)" type="number" value={formData.vacation_fund} onChange={(v) => handleInputChange('vacation_fund', v)} placeholder="Travel fund" icon={<FaGlobe className="text-green-400" />} />
              <InputField label="Emergency Fund Target (₹)" type="number" value={formData.emergency_fund_target} onChange={(v) => handleInputChange('emergency_fund_target', v)} placeholder="6 months expenses" icon={<FaShieldAlt className="text-red-400" />} />
            </div>

            {/* Other Goals - Dynamic Section */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-medium flex items-center">
                  <FaBullseye className="mr-2 text-purple-400" /> Other Financial Goals
                </h4>
                <button
                  onClick={handleAddOtherGoal}
                  className="px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-1 text-sm font-bold"
                  data-testid="add-other-goal-btn"
                >
                  <FaPlus size={12} />
                  <span>Add Goal</span>
                </button>
              </div>
              
              {(!formData.other_goals || formData.other_goals.length === 0) ? (
                <p className="text-gray-500 text-sm text-center py-4">Add any other financial goals like car purchase, business investment, charity, etc.</p>
              ) : (
                <div className="space-y-4">
                  {formData.other_goals.map((goal, index) => (
                    <div key={index} className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-purple-400 font-medium">Goal {index + 1}</span>
                        <button onClick={() => handleRemoveOtherGoal(index)} className="text-red-400 hover:text-red-300 p-1">
                          <FaTrash size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <InputField label="Goal Name" value={goal.goal_name} onChange={(v) => handleOtherGoalChange(index, 'goal_name', v)} placeholder="e.g., Buy Car" small />
                        <InputField label="Target Amount (₹)" type="number" value={goal.target_amount} onChange={(v) => handleOtherGoalChange(index, 'target_amount', v)} placeholder="Goal amount" small />
                        <InputField label="Target Year" type="number" value={goal.target_year} onChange={(v) => handleOtherGoalChange(index, 'target_year', v)} placeholder="e.g., 2027" small />
                        <InputField label="Current Savings (₹)" type="number" value={goal.current_savings} onChange={(v) => handleOtherGoalChange(index, 'current_savings', v)} placeholder="Already saved" small />
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Priority</label>
                          <select
                            value={goal.priority}
                            onChange={(e) => handleOtherGoalChange(index, 'priority', e.target.value)}
                            className="w-full px-3 py-2 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white text-sm focus:border-[#FFD700] focus:outline-none"
                          >
                            <option value="high">High Priority</option>
                            <option value="medium">Medium Priority</option>
                            <option value="low">Low Priority</option>
                          </select>
                        </div>
                        <InputField label="Notes" value={goal.notes} onChange={(v) => handleOtherGoalChange(index, 'notes', v)} placeholder="Any notes" small />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Risk Profile */}
            <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
              <h4 className="text-white font-medium mb-3">Risk Profile</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Risk Tolerance</label>
                  <select
                    value={formData.risk_tolerance}
                    onChange={(e) => handleInputChange('risk_tolerance', e.target.value)}
                    className="w-full px-4 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  >
                    <option value="conservative">Conservative (Low Risk)</option>
                    <option value="moderate">Moderate (Balanced)</option>
                    <option value="aggressive">Aggressive (High Risk)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Investment Experience</label>
                  <select
                    value={formData.investment_experience}
                    onChange={(e) => handleInputChange('investment_experience', e.target.value)}
                    className="w-full px-4 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Investment Horizon</label>
                  <select
                    value={formData.investment_horizon}
                    onChange={(e) => handleInputChange('investment_horizon', e.target.value)}
                    className="w-full px-4 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  >
                    <option value="0-3 years">Short Term (0-3 years)</option>
                    <option value="3-5 years">Medium Term (3-5 years)</option>
                    <option value="5-10 years">Long Term (5-10 years)</option>
                    <option value="10+ years">Very Long Term (10+ years)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default ClientDataForm;
